import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Loader2, CheckCircle2, ArrowRight } from "lucide-react";
import Seo from "../components/site/Seo";
import { resetPassword, apiError, validatePassword } from "../lib/api";

const FIELD = "w-full border border-border bg-secondary px-4 py-3 text-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-brand";
const LOGIN_PATHS = { admin: "/admin/login", client: "/client-portal", candidate: "/candidate-portal" };

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [role, setRole] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    const err = validatePassword(p1);
    if (err) return toast.error(err);
    if (p1 !== p2) return toast.error("Passwords do not match.");
    setSaving(true);
    try {
      const res = await resetPassword(token, p1);
      setRole(res.role || "");
      setDone(true);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not reset password.");
    } finally {
      setSaving(false);
    }
  };

  const loginPath = LOGIN_PATHS[role] || "/admin/login";

  return (
    <div className="grid min-h-screen place-items-center bg-background px-5" data-testid="reset-password-page">
      <Seo title="Reset Password" description="Set a new VectorBridge account password." />
      <div className="tech-grid pointer-events-none fixed inset-0 opacity-30 mask-fade-b" />
      <div className="relative w-full max-w-md border border-border bg-card p-8 shadow-xl sm:p-10">
        <Link to="/" className="flex items-center gap-2.5">
          <img src="/vbg-bridge-mark-t.png" alt="VectorBridge Group" className="h-9 w-auto" />
          <span className="font-display text-lg font-bold uppercase tracking-tight text-navy">VectorBridge</span>
        </Link>

        {!token ? (
          <div className="mt-8" data-testid="reset-invalid">
            <h1 className="font-display text-2xl font-light tracking-tighter text-navy">Invalid reset link.</h1>
            <p className="mt-3 text-sm text-muted-foreground">This link is missing its token. Please request a new password reset from the login screen.</p>
          </div>
        ) : done ? (
          <div className="mt-8 text-center" data-testid="reset-success">
            <CheckCircle2 size={40} className="mx-auto text-brand" />
            <h1 className="mt-5 font-display text-2xl font-light tracking-tighter text-navy">Password updated.</h1>
            <p className="mt-3 text-sm text-muted-foreground">You can now sign in with your new password.</p>
            <Link to={loginPath} data-testid="reset-go-login" className="group mt-6 inline-flex items-center justify-center gap-2 bg-brand px-6 py-4 font-mono text-xs uppercase tracking-[0.15em] text-white transition-colors hover:bg-navy">
              Go to login <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        ) : (
          <>
            <div className="mt-8 flex items-center gap-2 text-brand"><Lock size={16} /><span className="font-mono text-[11px] uppercase tracking-[0.2em]">Set a new password</span></div>
            <h1 className="mt-3 font-display text-3xl font-light tracking-tighter text-navy">Choose your new password.</h1>
            <form onSubmit={submit} data-testid="reset-form" className="mt-8 space-y-4">
              <input type="password" value={p1} onChange={(e) => setP1(e.target.value)} placeholder="New password" className={FIELD} data-testid="reset-new" />
              <input type="password" value={p2} onChange={(e) => setP2(e.target.value)} placeholder="Confirm new password" className={FIELD} data-testid="reset-confirm" />
              <p className="text-[11px] leading-relaxed text-muted-foreground">Must be at least 10 characters and include an uppercase letter, a number, and a symbol.</p>
              <button type="submit" disabled={saving} data-testid="reset-submit" className="inline-flex w-full items-center justify-center gap-2 bg-brand px-6 py-4 font-mono text-xs uppercase tracking-[0.15em] text-white transition-colors hover:bg-navy disabled:opacity-60">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />} Update password
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
